import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const CATEGORIES = [
  { value: 'raw_materials',    label: 'Raw Materials',      factor: 0.42 },
  { value: 'packaging',        label: 'Packaging',          factor: 0.89 },
  { value: 'logistics',        label: 'Logistics & Freight', factor: 0.11 },
  { value: 'it_equipment',     label: 'IT Equipment',       factor: 0.62 },
  { value: 'office_supplies',  label: 'Office Supplies',    factor: 0.35 },
  { value: 'professional_svc', label: 'Professional Services', factor: 0.18 },
  { value: 'utilities',        label: 'Utilities',          factor: 0.82 },
  { value: 'travel',           label: 'Business Travel',    factor: 0.255 },
];

const RESOURCES = [
  { title: 'GHG Protocol Scope 3 Standard', format: 'PDF', desc: 'Official methodology for Scope 3 Category 1 calculation', link: '#' },
  { title: 'Scope 3 Calculation for SMEs', format: 'Guide', desc: 'Simplified approach for supplier carbon footprinting', link: '#' },
  { title: 'BRSR Overview for Suppliers', format: 'Article', desc: 'India SEBI BRSR reporting obligations and timelines', link: '#' },
  { title: 'Carbon Footprint of Purchased Goods', format: 'Video', desc: 'How to estimate embodied carbon in supply chain', link: '#' },
];

const FORMAT_COLORS = { PDF: 'var(--neon-orange)', Guide: 'var(--neon-cyan)', Article: 'var(--neon-green)', Video: 'var(--neon-yellow)' };

export default function GreenProcurement() {
  const [logs, setLogs]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm]       = useState({ activity_type: 'raw_materials', quantity: '', supplier: '', notes: '', period: '2025-Q4' });

  const selectedCat   = CATEGORIES.find((c) => c.value === form.activity_type);
  // quantity = spend in INR lakhs; factor = kg CO2e per INR lakh
  const co2eEstimate  = form.quantity && selectedCat
    ? Math.round(parseFloat(form.quantity) * selectedCat.factor * 100) / 100 : null;

  const load = () => {
    api.get('/services/logs?module=procurement').then((r) => setLogs(r.data.logs || [])).catch(() => {});
    api.get('/services/summary').then((r) => setSummary(r.data.summary?.procurement)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.quantity) { setError('Annual spend is required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/services/logs', {
        module: 'procurement', activity_type: form.activity_type,
        quantity: parseFloat(form.quantity), unit: 'inr_lakh',
        supplier: form.supplier, notes: form.notes, period: form.period,
      });
      setSuccess('Procurement category logged. Scope 3.1 updated.');
      setForm((f) => ({ ...f, quantity: '', supplier: '', notes: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log procurement entry.');
    } finally { setSubmitting(false); }
  };

  const totalScope3  = logs.reduce((s, l) => s + (l.co2e_emitted_kg || 0), 0);
  const totalSpend   = logs.reduce((s, l) => s + (l.quantity || 0), 0);

  // Top 3 categories by CO2e
  const byCategory   = logs.reduce((acc, l) => {
    acc[l.activity_type] = (acc[l.activity_type] || 0) + (l.co2e_emitted_kg || 0);
    return acc;
  }, {});
  const topCategories = Object.entries(byCategory).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Green Services — Procurement</div>
        <h1>Sustainable Procurement</h1>
        <p className="section-copy">Track Scope 3 Category 1 emissions from purchased goods and services. Identify high-impact categories and switch to low-carbon suppliers.</p>
      </section>

      {/* Stats */}
      <section className="stats-grid">
        <div className="surface-panel stat-card">
          <div className="stat-label">Scope 3 Cat. 1 total</div>
          <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>
            {(totalScope3 / 1000).toFixed(2)} <span style={{ fontSize: '1rem' }}>tCO2e</span>
          </div>
          <div className="stat-sub">purchased goods &amp; services</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">Total spend tracked</div>
          <div className="stat-value" style={{ color: 'var(--neon-cyan)' }}>
            ₹{totalSpend.toFixed(1)} <span style={{ fontSize: '1rem' }}>L</span>
          </div>
          <div className="stat-sub">INR lakhs across categories</div>
        </div>
        <div className="surface-panel stat-card">
          <div className="stat-label">Avg emission intensity</div>
          <div className="stat-value" style={{ color: 'var(--neon-yellow)' }}>
            {totalSpend > 0 ? ((totalScope3 / 1000) / totalSpend * 100).toFixed(2) : '—'} <span style={{ fontSize: '0.85rem' }}>tCO2e/L</span>
          </div>
          <div className="stat-sub">per INR lakh spent</div>
        </div>
        <Link to="/vendors?category=supplier" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Low-carbon vendors</div>
            <div className="stat-value" style={{ color: 'var(--neon-green)' }}>3</div>
            <div className="stat-sub">verified green suppliers</div>
          </div>
        </Link>
      </section>

      {/* Top emission categories */}
      {topCategories.length > 0 && (
        <section className="surface-panel glow-surface" style={{ padding: '1.5rem' }}>
          <div className="section-tag" style={{ marginBottom: '1rem' }}>Top Scope 3 hotspots — procurement</div>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {topCategories.map(([cat, kg], i) => {
              const pct = totalScope3 > 0 ? Math.round((kg / totalScope3) * 100) : 0;
              const label = CATEGORIES.find((c) => c.value === cat)?.label || cat;
              return (
                <div key={cat} style={{ display: 'grid', gridTemplateColumns: '1.5rem 1fr auto', gap: '1rem', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-cyan)' }}>#{i + 1}</div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                      <span style={{ fontSize: '0.82rem' }}>{label}</span>
                      <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', color: 'var(--neon-orange)' }}>{(kg / 1000).toFixed(2)} tCO2e</span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: 'var(--neon-orange)', borderRadius: '2px' }} />
                    </div>
                  </div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: 'var(--muted2)', minWidth: '2.5rem', textAlign: 'right' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Log Form */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Log procurement category</div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">Category *</label>
              <select className="form-input" value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Annual spend (INR lakhs) *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 50" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Supplier (optional)</label>
              <input className="form-input" type="text" placeholder="e.g. ABC Raw Materials Ltd" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Period</label>
              <select className="form-input" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}>
                {['2025-Q3', '2025-Q4', '2026-Q1', '2026-Q2'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {selectedCat && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(255,100,50,0.07)', borderRadius: '8px', border: '1px solid rgba(255,100,50,0.2)', marginBottom: '1rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div><div className="stat-label">Emission intensity</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: 'var(--neon-orange)' }}>{selectedCat.factor} kg CO2e / INR lakh</div></div>
              {co2eEstimate !== null && <div><div className="stat-label">Est. Scope 3 this entry</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: 'var(--neon-orange)' }}>{co2eEstimate} kg CO2e</div></div>}
              <div><div className="stat-label">GHG Protocol category</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.75rem', color: 'var(--muted2)' }}>Scope 3, Cat. 1</div></div>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Notes</label>
            <input className="form-input" type="text" placeholder="e.g. Includes FY2025-Q4 PO data" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          {error && <div style={{ color: 'var(--neon-pink)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{error}</div>}
          {success && <div style={{ color: 'var(--neon-green)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Logging…' : 'Log procurement'}</button>
        </form>
      </section>

      {/* Low-carbon sourcing suggestions */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
          <div className="section-tag">Low-carbon sourcing suggestions</div>
          <Link to="/vendors?category=supplier" className="btn btn-secondary btn-sm">Browse green vendors →</Link>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {[
            { category: 'Raw Materials', action: 'Switch to recycled-content or bio-based inputs', reduction: 35, link: '/vendors?category=supplier' },
            { category: 'Packaging',     action: 'Replace virgin plastic with mono-material recycled alternatives', reduction: 55, link: '/services/packaging' },
            { category: 'Logistics',     action: 'Shift freight to rail or EV fleet', reduction: 70, link: '/services/logistics' },
            { category: 'IT Equipment',  action: 'Source refurbished equipment to cut embodied carbon', reduction: 60, link: '/vendors?category=supplier' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '1.5rem', alignItems: 'center', padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: 'var(--neon-cyan)', marginBottom: '0.25rem' }}>{s.category}</div>
                <div className="stat-sub">{s.action}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div className="stat-label">CO2e reduction</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-green)' }}>-{s.reduction}%</div>
              </div>
              <Link to={s.link} className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Find vendor</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Digital Carbon Academy */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1.25rem' }}>Digital Carbon Academy — supplier resources</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {RESOURCES.map((r) => (
            <div key={r.title} style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="badge" style={{ color: FORMAT_COLORS[r.format], borderColor: FORMAT_COLORS[r.format], fontSize: '0.6rem' }}>{r.format}</span>
              </div>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.85rem' }}>{r.title}</div>
              <div className="stat-sub" style={{ flex: 1 }}>{r.desc}</div>
              <a href={r.link} className="btn btn-secondary btn-sm" style={{ fontSize: '0.65rem', textDecoration: 'none', textAlign: 'center' }}>Access resource →</a>
            </div>
          ))}
        </div>
      </section>

      {/* Procurement Log */}
      {logs.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag" style={{ marginBottom: '1rem' }}>Procurement activity log</div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Category</th><th>Spend (₹L)</th><th>Supplier</th><th>Scope 3 (kgCO2e)</th></tr></thead>
            <tbody>
              {logs.slice(0, 10).map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontSize: '0.85rem' }}>{CATEGORIES.find((c) => c.value === l.activity_type)?.label || l.activity_type}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem' }}>₹{l.quantity?.toLocaleString('en-IN')}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{l.supplier || '—'}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>{l.co2e_emitted_kg?.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
